import { ClientNinetailedExperience } from './ClientNinetailedExperience';
import { ServerNinetailedExperience } from './ServerNinetailedExperience';
import { getNinetailedData } from 'libs/contentful/queries/ninetailed';
import { ComponentType } from 'react';
import { mappedModules } from 'utils/modules';
import { PageSectionProps } from 'components/PageSection/types';
import PageSection from 'components/PageSection/PageSection';
import ClientPageSection from 'components/PageSection/ClientPageSection';
import { getNinetailedMode, shouldRenderBaseline } from 'utils/serverExperiences';
import { ModuleProps, ModuleTypeName } from 'utils/modules';
import ClientWrapper from './ClientWrapper';
import { ContentfulModule } from 'libs/contentful/types';
import { NinetailedModuleData } from 'libs/contentful/queries/page/ninetailed';
import { filterValidExperiences } from 'utils/experiences';
import { isSectionModule } from 'utils/sectionModules';

interface NinetailedExperienceProps<T extends ContentfulModule> {
  entryId: string;
  entry?: T;
  hasExperiments?: boolean;
  ninetailedData?: NinetailedModuleData;
}

export const getPageSectionModule = async (): Promise<ComponentType<PageSectionProps>> => {
  return (await getNinetailedMode()) === 'client' ? ClientPageSection : PageSection;
};

const getModule = async (moduleName: string) => {
  if (moduleName === 'PageSection') {
    return await getPageSectionModule();
  } else {
    return mappedModules[moduleName as keyof typeof mappedModules] as ComponentType<PageSectionProps>;
  }
};

/**
 * Renders a Ninetailed experience for a given component
 * @param entryId - The entry ID of the component
 * @param module - The module to render
 * @param entry - The entry data (optional, used to get __typename)
 * @param hasExperiments - Whether the page has experiments (optional)
 * @param ninetailedData - Pre-fetched Ninetailed data (optional)
 * @returns The rendered component
 */
export const NinetailedExperience = async <T extends ContentfulModule>({
  entryId,
  entry,
  hasExperiments = false,
  ninetailedData,
}: NinetailedExperienceProps<T>) => {
  const moduleName = entry?.__typename || '';

  // Use pre-fetched data if available, otherwise fetch it
  const { baselineEntry, mappedExperiences } = ninetailedData || (await getNinetailedData(entryId, moduleName));

  if (!baselineEntry || !baselineEntry.sys?.id) {
    console.error(`Baseline entry not found or missing sys.id for entryId: ${entryId}`);
    return null;
  }

  const baselineEntryProps = baselineEntry as unknown as PageSectionProps & ModuleProps[ModuleTypeName];
  const experiences = filterValidExperiences(mappedExperiences);

  const finalProps =
    moduleName === 'PageSection' ? baselineEntryProps : isSectionModule(moduleName) ? entry : baselineEntryProps;

  const Module = await getModule(moduleName);

  if (!Module) {
    console.error(`Module ${moduleName} not found in mappedModules`);
    return null;
  }

  if (!hasExperiments || shouldRenderBaseline()) {
    return <Module {...(finalProps as unknown as PageSectionProps & ModuleProps[ModuleTypeName])} />;
  }

  if ((await getNinetailedMode()) === 'client') {
    if (moduleName === 'PageSection') {
      return (
        <ClientNinetailedExperience
          id={baselineEntry.sys.id}
          module={Module}
          passthroughProps={baselineEntryProps}
          experiences={experiences}
          hasExperiments={hasExperiments}
        />
      );
    }

    return (
      <ClientWrapper
        id={baselineEntry.sys.id}
        moduleType={moduleName as ModuleTypeName}
        passthroughProps={finalProps as unknown as PageSectionProps & ModuleProps[ModuleTypeName]}
        experiences={experiences}
        hasExperiments={hasExperiments}
      />
    );
  } else {
    const { headers } = await import('next/headers');
    const nonce = (await headers()).get('x-nonce') || undefined;
    return (
      <ServerNinetailedExperience
        id={baselineEntry.sys.id}
        component={Module}
        passthroughProps={finalProps as unknown as PageSectionProps & ModuleProps[ModuleTypeName]}
        experiences={experiences}
        nonce={nonce}
      />
    );
  }
};
